// lib
import React from 'react'
import PropTypes from 'prop-types'
import { CompositeDecorator } from 'draft-js'

// components
import ErrorsDecoratorPopup from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/decorators/ErrorsDecoratorPopup'

// lib
import errorAnchorText from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/decorators/error-anchor-text'

// styles
import style from '@aytm/lib/rfd-ui/styles/advanced/draft-js-decorators.module.scss'

// constants
export const ERROR_TYPES = {
  ref: {
    text: 'questionDoesntExist',
    type: 'warning',
    color: '#F7931E',
    style: style.textWarning
  },
  alias: {
    text: 'aliasError',
    type: 'warning',
    color: '#F7931E',
    style: style.textWarning
  },
  cluster: {
    text: 'clusterDoesntExist',
    type: 'warning',
    color: '#F7931E',
    style: style.textWarning
  },
  trait: {
    text: 'traitDoesntExist',
    type: 'error',
    color: '#9D0000',
    style: style.textError
  },
  sref: {
    text: 'serverReferenceError',
    type: 'warning',
    color: '#F7931E',
    style: style.textWarning
  },
  parse: {
    text: 'notValidRule',
    type: 'error',
    color: '#9D0000',
    style: style.textError
  },
  aytm_logic_required: {
    text: 'upgradeRequiredText',
    type: 'warning',
    color: '#F7931E',
    style: style.textWarning
  },
  old: {
    text: 'referenceIsOutdated',
    type: 'warning-special',
    color: '#d05817',
    style: style.textOld
  },
  invalid_logic: {
    color: '#9D0000',
    text: 'notValidRule',
    type: 'error',
    style: style.textError
  },
  overlimit: {
    color: '#9D0000',
    text: 'outdatedLocale',
    type: 'error',
    style: style.textError
  },
  overlimit_skipper: {
    color: '#9D0000',
    text: 'overlimitSkipper',
    type: 'error',
    style: style.textError
  },
  outdated: {
    color: '#9D0000',
    text: 'outdatedLocale',
    type: 'error',
    style: style.textError
  },
  outdated_skipper: {
    color: '#9D0000',
    text: 'outdatedLocale',
    type: 'error',
    style: style.textError
  },
  outdated_no_translate: {
    color: '#9D0000',
    text: 'outdatedLocale',
    type: 'error',
    style: style.textError
  }
}

const createErrorsDecoratorComponent = (store, errorTypes = ERROR_TYPES) => {
  const ErrorsDecoratorComponent = props => {
    const errors = store.getErrors()
    return errors ? (
      <ErrorsDecoratorPopup
        locale={store.getLocale()}
        errors={errors}
        textProps={props}
        errorTypes={errorTypes}
        updateOldRefs={store.updateOldRefs}
      />
    ) : (
      props.children
    )
  }

  ErrorsDecoratorComponent.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
  }

  return ErrorsDecoratorComponent
}

const createErrorsDecoratorStrategy = (store, errorTypes) => (contentBlock, callback) => {
  const text =
    'function' === typeof store.strategy.processText
      ? store.strategy.processText(contentBlock.getText())
      : contentBlock.getText()

  if (!text) {
    return
  }
  const errorTypeFilter = store.strategy.errorTypes?.length
    ? error => -1 !== store.strategy.errorTypes.indexOf(error.get('type'))
    : errorTypes
      ? error => errorTypes[error.get('type')]
      : _ => true

  // an upgrade prompt anchors on the whole statement, so it would claim the slice a narrower
  // validity error needs - the validity error takes the text first
  const promptRank = error => (error.has('upgradePackage') ? 1 : 0)
  const errors = (store.getErrors() || [])
    .filter(error => errorAnchorText(error) && errorTypeFilter(error))
    .sort((a, b) => promptRank(a) - promptRank(b) || errorAnchorText(b).length - errorAnchorText(a).length)

  if (!errors || !errors.size) {
    return
  }

  if ('function' === typeof store.strategy.detectError) {
    errors.forEach(error => {
      try {
        store.strategy.detectError(errors, error, text, callback)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    })
  }

  if ('function' === typeof store.strategy.detectErrors) {
    store.strategy.detectErrors(errors, text, callback)
  }
}

const ErrorsDecoratorFactory = (store, errorTypes) =>
  new CompositeDecorator([
    {
      component: createErrorsDecoratorComponent(store, errorTypes),
      strategy: createErrorsDecoratorStrategy(store, errorTypes)
    }
  ])

export default ErrorsDecoratorFactory
